import type { ProblemDetails } from "@workith/shared";

const CORRELATION_HEADER = "x-correlation-id";

export class FrontendApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly retryable: boolean,
    readonly correlationId: string
  ) {
    super(message);
    this.name = "FrontendApiError";
  }
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
}

export async function normalizeApiError(response: Response): Promise<FrontendApiError> {
  const contentType = response.headers.get("content-type") ?? "";
  const headerCorrelationId = response.headers.get(CORRELATION_HEADER) ?? "";
  const fallbackMessage = `Request failed (${response.status})`;

  if (!contentType.includes("json")) {
    return new FrontendApiError(
      fallbackMessage,
      response.status,
      "HTTP_ERROR",
      false,
      headerCorrelationId
    );
  }

  let body: Record<string, unknown> | null = null;
  try {
    body = objectValue(await response.json());
  } catch {
    // Invalid JSON is intentionally reduced to status and correlation ID.
  }

  if (contentType.includes("application/problem+json") && body) {
    const problem = body as Partial<ProblemDetails>;
    return new FrontendApiError(
      typeof problem.title === "string" ? problem.title : fallbackMessage,
      typeof problem.status === "number" ? problem.status : response.status,
      typeof problem.code === "string" ? problem.code : "HTTP_ERROR",
      problem.retryable === true,
      typeof problem.correlationId === "string" ? problem.correlationId : headerCorrelationId
    );
  }

  const legacyMessage = body && typeof body.error === "string" ? body.error : fallbackMessage;
  return new FrontendApiError(
    legacyMessage,
    response.status,
    "LEGACY_API_ERROR",
    false,
    headerCorrelationId
  );
}
